import { get, isEmpty } from 'lodash';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import useFrustrationSignalQuery, {
  createFrustrationSignalQuery,
} from './useFrustrationSignalQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { panelsAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { calcDodInterval } from '../../utils';

const getFrustrationSignalMetrics = (signalMeta) => {
  try {
    if (!signalMeta?.metrics) {
      return [];
    }

    const { count, with_user, with_session } = signalMeta.metrics;
    return [count, with_user, with_session].filter(Boolean);
  } catch (error) {
    // Error getting frustration signal metrics
    return [];
  }
};

function useFetchDodFrustrationSignalData({
  signalId,
  signalMeta,
  enabled = true,
}: {
  signalId: string;
  signalMeta?: any;
  enabled?: boolean;
}) {
  const panels = useAtomValue(panelsAtom);
  const metrics = getFrustrationSignalMetrics(signalMeta);
  const { interval, filter, _rKey, allowedMetrics } = useQueryParams();

  const isDeleted = signalMeta?.isDeleted;

  const filteredMetrics = metrics.filter(
    (metric) => metric && allowedMetrics && metric in allowedMetrics,
  );

  // Get percentage metrics for the allowed metrics
  const percentageMetrics = filteredMetrics
    .map((metric) => getPercentageMetricByKey({ metricKey: metric }))
    .filter(Boolean)
    .filter((metric) => allowedMetrics && metric in allowedMetrics);

  // Combine base metrics with their percentage counterparts
  const allowedMetricList = [...new Set([...filteredMetrics, ...percentageMetrics])];

  // Calculate comparison interval (previous period)
  const comparisonInterval = useMemo(() => calcDodInterval(interval), [interval]);

  const options = {};
  const payload = comparisonInterval
    ? createFrustrationSignalQuery(
        allowedMetricList,
        `${comparisonInterval.from}/${comparisonInterval.to}`,
        comparisonInterval.granularity,
        filter,
        options,
      )
    : null;

  const result = useFrustrationSignalQuery({
    signalId: `${signalId}-dod`,
    queryKey: ['overview', 'frustration-signal-dod', signalId, payload?.queries, _rKey],
    queryFn: () =>
      payload
        ? getMetricsData(payload, {}, instance).then((response) => {
            const { data, totals, annotations } = get(response, ['data', '0'], {
              totals: {},
              data: [],
              annotations: [],
            });
            const formattedData = formatTimeseriesData({
              metrics: allowedMetricList,
              data,
              interval: comparisonInterval,
              annotations,
            });
            return { data: formattedData, totals };
          })
        : Promise.resolve({ data: {}, totals: {} }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled:
        enabled &&
        !!metrics.length &&
        !isDeleted &&
        !!comparisonInterval &&
        !isEmpty(interval) &&
        panels.frustration,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
    },
  });

  if (isDeleted || !enabled || !comparisonInterval) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
      queued: false,
      comparisonInterval,
    };
  }

  return {
    ...result,
    queued: result.queued || false,
    comparisonInterval,
  };
}

export default useFetchDodFrustrationSignalData;
